import { useMutation } from '@tanstack/react-query'
import { Box, Flex, Text } from '@radix-ui/themes'
import { useDropzone } from 'react-dropzone'
import cn from 'classnames'
import { Ops } from '@willie/schemas'
import { useAuth } from '~/auth/AuthProvider'
import { uploadMeterPhotos, type MeterPhotoRejection } from '../service'
import { useInvalidateMeterPhotos } from '../useInvalidateMeterPhotos'

const REJECTION_LABEL: Record<MeterPhotoRejection['reason'], string> = {
  'unsupported-format': 'not a JPEG, PNG, WebP or HEIC photo',
  'too-large': `over ${Ops.InstallationAttachments.MAX_PHOTO_BYTES / 1024 / 1024} MB, shrink it and drop it again`,
  'upload-failed': 'upload failed, try again'
}

// Accepts any file on purpose: customers send PDFs and spreadsheets, and ops should see which were refused.
export const MeterPhotoDropZone = ({ installationId }: { installationId: number }) => {
  const { supabaseClient, session } = useAuth()
  const invalidate = useInvalidateMeterPhotos()
  const upload = useMutation({
    mutationFn: (files: File[]) =>
      uploadMeterPhotos(supabaseClient, { installationId, files, uploadedBy: session?.user.email ?? null }),
    // Settled, not success: a batch that failed halfway still stored some photos.
    onSettled: invalidate
  })

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    multiple: true,
    disabled: upload.isPending,
    onDrop: (files) => {
      if (files.length > 0) upload.mutate(files)
    }
  })

  return (
    <Flex direction="column" gap="1">
      <Box
        {...getRootProps()}
        className={cn(
          'cursor-pointer rounded-[var(--radius-3)] border border-dashed p-3 text-center',
          isDragActive ? 'border-[color:var(--accent-9)] bg-[var(--accent-2)]' : 'border-[color:var(--gray-6)]'
        )}
      >
        <input {...getInputProps()} />
        <Text size="2" color="gray">
          {upload.isPending ? 'Uploading…' : 'Drop meter photos here, or click to pick'}
        </Text>
      </Box>
      {(upload.data ?? []).map((rejection, index) => (
        <Text key={`${rejection.filename}-${index}`} size="1" color="amber">
          {rejection.filename}: {REJECTION_LABEL[rejection.reason]}
        </Text>
      ))}
      {upload.error ? (
        <Text size="1" color="red">
          {upload.error.message}
        </Text>
      ) : null}
    </Flex>
  )
}
